/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import { Avatar, AvatarProps } from "@mui/joy";
import { useMemo, useState } from "react";

interface CharacterAvatarProps extends Omit<AvatarProps, "children"> {
  name?: string;
  src?: string;
  loading?: "lazy" | "eager";
  priority?: boolean;
}

// Color mapping for each letter (using pastel colors for better visibility)
const letterColors: { [key: string]: string } = {
  A: "rgba(255, 99, 132, 0.2)", // Pink
  B: "rgba(54, 162, 235, 0.2)", // Blue
  C: "rgba(255, 206, 86, 0.2)", // Yellow
  D: "rgba(75, 192, 192, 0.2)", // Teal
  E: "rgba(153, 102, 255, 0.2)", // Purple
  F: "rgba(255, 159, 64, 0.2)", // Orange
  G: "rgba(199, 199, 199, 0.2)", // Gray
  H: "rgba(83, 102, 255, 0.2)", // Indigo
  I: "rgba(255, 99, 132, 0.2)", // Pink
  J: "rgba(54, 162, 235, 0.2)", // Blue
  K: "rgba(255, 206, 86, 0.2)", // Yellow
  L: "rgba(75, 192, 192, 0.2)", // Teal
  M: "rgba(153, 102, 255, 0.2)", // Purple
  N: "rgba(255, 159, 64, 0.2)", // Orange
  O: "rgba(199, 199, 199, 0.2)", // Gray
  P: "rgba(83, 102, 255, 0.2)", // Indigo
  Q: "rgba(255, 99, 132, 0.2)", // Pink
  R: "rgba(54, 162, 235, 0.2)", // Blue
  S: "rgba(255, 206, 86, 0.2)", // Yellow
  T: "rgba(75, 192, 192, 0.2)", // Teal
  U: "rgba(153, 102, 255, 0.2)", // Purple
  V: "rgba(255, 159, 64, 0.2)", // Orange
  W: "rgba(199, 199, 199, 0.2)", // Gray
  X: "rgba(83, 102, 255, 0.2)", // Indigo
  Y: "rgba(255, 99, 132, 0.2)", // Pink
  Z: "rgba(54, 162, 235, 0.2)", // Blue
  OTHER: "rgba(171, 71, 188, 0.2)", // Default purple for non-letter characters
};

export function CharacterAvatar({
  name = "",
  src,
  loading = "lazy",
  priority = false,
  ...props
}: CharacterAvatarProps): JSX.Element {
  const [imageLoading, setImageLoading] = useState(!!src);
  const [imageError, setImageError] = useState(false);

  const firstLetter = useMemo(() => {
    return name.charAt(0).toUpperCase();
  }, [name]);

  // Get background color based on first letter
  const bgColor = useMemo(() => {
    if (!firstLetter) return letterColors.OTHER;
    return letterColors[firstLetter] || letterColors.OTHER;
  }, [firstLetter]);

  // Generate appropriate alt text
  const altText = useMemo(() => {
    if (name) {
      return `${name}'s avatar`;
    }
    return "Character avatar";
  }, [name]);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const shouldShowFallback = !src || imageError;

  return (
    <Avatar
      alt={altText}
      {...props}
      sx={{
        ...props.sx,
        bgcolor: shouldShowFallback ? bgColor : undefined,
        color: shouldShowFallback ? "neutral.500" : undefined,
        fontWeight: shouldShowFallback ? "bold" : undefined,
        // Prevent layout shift during image loading
        position: "relative",
        overflow: "hidden",
        aspectRatio: "1 / 1", // Ensure square aspect ratio
        "& img": {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "opacity 0.2s ease-in-out",
          opacity: imageLoading ? 0 : 1,
        },
      }}
    >
      {src && !imageError && (
        <img
          src={src}
          alt={altText}
          loading={priority ? "eager" : loading}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
      {shouldShowFallback && (
        <>
          {imageLoading && (
            <div
              className="loading-skeleton"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                borderRadius: "inherit",
              }}
            />
          )}
          {!imageLoading && firstLetter}
        </>
      )}
    </Avatar>
  );
}
